/**
 * Google Digital Goods API Service for PWA Billing
 * Handles subscription purchases through Google Play Store
 * 
 * IMPORTANT: Prices are ALWAYS fetched from Google Play, never hardcoded
 * Google handles: currencies, taxes (VAT), regional pricing, compliance
 * 
 * TROUBLESHOOTING:
 * - If products don't load: Check assetlinks.json SHA-256 matches Play App Signing
 * - If purchase fails: Verify product ID matches Play Console exactly
 * - If billing unavailable: App must be installed from Play Store as TWA
 */

// Product IDs - MUST match exactly what's configured in Google Play Console
// Format: lowercase, underscores, no spaces
export const PRODUCT_IDS = {
  PREMIUM_MONTHLY: 'premium_monthly_1999',
  PREMIUM_ANNUAL: 'premium_annual_14999',
  // Add backup IDs in case naming changed
  PREMIUM_MONTHLY_ALT: 'premium_monthly',
  PREMIUM_MONTHLY_ALT2: 'fatandslim_premium_monthly',
};

// All product IDs to try (in order of preference)
const ALL_PRODUCT_IDS = [
  PRODUCT_IDS.PREMIUM_MONTHLY,
  PRODUCT_IDS.PREMIUM_MONTHLY_ALT,
  PRODUCT_IDS.PREMIUM_MONTHLY_ALT2,
];

// Google Play Billing service URL
const PLAY_BILLING_URL = 'https://play.google.com/billing';

// Debug flag - enable for troubleshooting
const DEBUG_BILLING = true;

const log = (...args) => {
  if (DEBUG_BILLING) {
    console.log('[Billing]', ...args);
  }
};

const logError = (...args) => {
  console.error('[Billing]', ...args);
};

/**
 * Check if Digital Goods API is available
 * Only available when app is installed from Play Store as TWA
 */
export const isDigitalGoodsAvailable = () => {
  const available = 'getDigitalGoodsService' in window;
  log('Digital Goods API available:', available);
  
  if (!available) {
    log('Reason: App not installed from Play Store or not running as TWA');
  }
  
  return available;
};

/**
 * Get the Digital Goods Service instance
 */
export const getDigitalGoodsService = async () => {
  if (!isDigitalGoodsAvailable()) {
    log('Digital Goods API not available - app not installed from Play Store');
    return null;
  }

  try {
    log('Connecting to Digital Goods Service...');
    const service = await window.getDigitalGoodsService(PLAY_BILLING_URL);
    
    if (service) {
      log('Digital Goods Service connected successfully');
    } else {
      logError('Digital Goods Service returned null');
    }
    
    return service;
  } catch (error) {
    logError('Failed to get Digital Goods Service:', error.message);
    logError('Full error:', error);
    
    // Provide specific error guidance
    if (error.message?.includes('NotAllowedError')) {
      logError('DIAGNOSIS: App signature mismatch. Check SHA-256 in assetlinks.json');
    } else if (error.message?.includes('not supported')) {
      logError('DIAGNOSIS: Digital Goods API not supported in this context');
    }
    
    return null;
  }
};

/**
 * Get product details from Google Play
 * Tries multiple product IDs if the primary one fails
 */
export const getProductDetails = async (productIds = null) => {
  const service = await getDigitalGoodsService();
  
  if (!service) {
    log('Cannot fetch prices - Digital Goods API not available');
    return null;
  }

  // If no specific IDs provided, try all known IDs
  const idsToTry = productIds || ALL_PRODUCT_IDS;
  
  log('Attempting to fetch products with IDs:', idsToTry);

  try {
    // Try fetching with all IDs first
    const details = await service.getDetails(idsToTry);
    
    log('Raw response from Google Play:', JSON.stringify(details, null, 2));
    
    if (!details || details.length === 0) {
      logError('No products returned from Google Play');
      logError('DIAGNOSIS: Check that:');
      logError('  1. Product IDs match exactly in Play Console');
      logError('  2. Products are ACTIVE (not draft)');
      logError('  3. App is published (at least internal testing)');
      logError('  4. You are logged in with a test account');
      return null;
    }
    
    log(`Found ${details.length} product(s)`);
    
    return details.map(item => {
      log('Product:', item.itemId);
      log('  - Title:', item.title);
      log('  - formattedPrice:', item.formattedPrice);
      log('  - price object:', JSON.stringify(item.price));
      
      return {
        itemId: item.itemId,
        title: item.title,
        description: item.description,
        formattedPrice: item.formattedPrice,
        price: item.price,
        subscriptionPeriod: item.subscriptionPeriod,
      };
    });
    
  } catch (error) {
    logError('Failed to get product details:', error.message);
    logError('Full error:', error);
    
    // Try each ID individually to find which one works
    log('Trying product IDs individually...');
    
    for (const productId of idsToTry) {
      try {
        log(`Trying single ID: ${productId}`);
        const singleResult = await service.getDetails([productId]);
        
        if (singleResult && singleResult.length > 0) {
          log(`SUCCESS with ID: ${productId}`);
          return singleResult.map(item => ({
            itemId: item.itemId,
            title: item.title,
            description: item.description,
            formattedPrice: item.formattedPrice,
            price: item.price,
            subscriptionPeriod: item.subscriptionPeriod,
          }));
        }
      } catch (e) {
        log(`Failed with ID ${productId}:`, e.message);
      }
    }
    
    logError('All product IDs failed');
    return null;
  }
};

/**
 * Check existing purchases/entitlements
 */
export const checkExistingPurchases = async () => {
  const service = await getDigitalGoodsService();
  if (!service) {
    return [];
  }

  try {
    log('Checking existing purchases...');
    const purchases = await service.listPurchases();
    log('Existing purchases:', JSON.stringify(purchases, null, 2));
    return purchases || [];
  } catch (error) {
    logError('Failed to check purchases:', error.message);
    return [];
  }
};

/**
 * Initiate a purchase using Payment Request API
 */
export const purchaseSubscription = async (productId = PRODUCT_IDS.PREMIUM_MONTHLY) => {
  log('Starting purchase for:', productId);
  
  if (!('PaymentRequest' in window)) {
    throw new Error('Payment Request API not supported');
  }

  // Try to fetch product details (used only to pre-fill the price label).
  // IMPORTANT: this is NOT required to purchase — the Payment Request only needs
  // the SKU, and Google Play resolves the real product & price at checkout.
  // So we must NEVER block the purchase if getDetails is temporarily empty
  // (e.g. right after a price change or a fresh install, while Google re-propagates).
  let products = await getProductDetails([productId]);
  let product = products?.find(p => p.itemId === productId) || products?.[0] || null;

  if (!product) {
    log('getDetails returned nothing — proceeding with SKU anyway (Play resolves price at checkout)');
  }

  const sku = product?.itemId || productId;

  // Create payment method data for Google Play Billing
  const paymentMethodData = [{
    supportedMethods: PLAY_BILLING_URL,
    data: {
      sku: sku,
    },
  }];

  // Placeholder amount — Google Play always displays the REAL price. 0.00 is accepted.
  let amount = { currency: 'EUR', value: '0.00' };
  if (product?.price && typeof product.price === 'object' && product.price.value) {
    amount = {
      currency: product.price.currency || 'EUR',
      value: String(product.price.value),
    };
  }

  const paymentDetails = {
    total: {
      label: product?.title || 'Fat & Slim Premium',
      amount: amount,
    },
  };

  try {
    log('Creating PaymentRequest...');
    const request = new PaymentRequest(paymentMethodData, paymentDetails);

    // canMakePayment can be unreliable — log it but DON'T block the purchase on it.
    try {
      const canPay = await request.canMakePayment();
      log('Can make payment:', canPay);
    } catch (e) {
      log('canMakePayment check skipped:', e.message);
    }

    log('Showing payment UI...');
    const paymentResponse = await request.show();
    
    const { purchaseToken } = paymentResponse.details;
    log('Purchase token received:', purchaseToken?.substring(0, 20) + '...');
    
    await paymentResponse.complete('success');
    
    log('Purchase successful!');
    
    return {
      success: true,
      purchaseToken,
      productId: sku,
    };
  } catch (error) {
    logError('Purchase failed:', error.message);
    
    if (error.name === 'AbortError') {
      return { success: false, error: 'Paiement annulé' };
    }
    
    return { success: false, error: error.message };
  }
};

/**
 * Acknowledge a purchase
 * DEPRECATED / NON UTILISÉ : pour les ABONNEMENTS, l'acknowledgment est désormais
 * effectué CÔTÉ SERVEUR via la Google Play Developer API
 * (purchases.subscriptions.acknowledge), appelé automatiquement par le backend
 * dans /api/premium/verify. Ne pas utiliser cette fonction pour les abonnements.
 */
export const acknowledgePurchase = async (purchaseToken) => {
  const service = await getDigitalGoodsService();
  if (!service) {
    return false;
  }

  try {
    log('Acknowledging purchase...');
    await service.acknowledge(purchaseToken, 'onetime');
    log('Purchase acknowledged successfully');
    return true;
  } catch (error) {
    logError('Failed to acknowledge purchase:', error.message);
    return false;
  }
};

/**
 * Consume a purchase (for consumables, not subscriptions)
 */
export const consumePurchase = async (purchaseToken) => {
  const service = await getDigitalGoodsService();
  if (!service) {
    return false;
  }

  try {
    await service.consume(purchaseToken);
    log('Purchase consumed');
    return true;
  } catch (error) {
    logError('Failed to consume purchase:', error.message);
    return false;
  }
};

/**
 * Diagnostic function - call this to debug billing issues
 */
export const runBillingDiagnostics = async () => {
  console.log('=== BILLING DIAGNOSTICS ===');
  console.log('1. Digital Goods API available:', isDigitalGoodsAvailable());
  
  const service = await getDigitalGoodsService();
  console.log('2. Service connection:', service ? 'OK' : 'FAILED');
  
  if (service) {
    console.log('3. Testing product IDs...');
    for (const id of ALL_PRODUCT_IDS) {
      try {
        const result = await service.getDetails([id]);
        console.log(`   - ${id}:`, result?.length > 0 ? 'FOUND' : 'NOT FOUND');
      } catch (e) {
        console.log(`   - ${id}: ERROR - ${e.message}`);
      }
    }
    
    console.log('4. Checking existing purchases...');
    const purchases = await checkExistingPurchases();
    console.log('   Purchases found:', purchases.length);
  }
  
  console.log('=== END DIAGNOSTICS ===');
};

export default {
  isDigitalGoodsAvailable,
  getDigitalGoodsService,
  getProductDetails,
  checkExistingPurchases,
  purchaseSubscription,
  acknowledgePurchase,
  consumePurchase,
  runBillingDiagnostics,
  PRODUCT_IDS,
  ALL_PRODUCT_IDS,
};
